<script setup lang="ts">
/*
  Asks a phone to go fullscreen, once, and then never again.

  ── Why ask at all, when the deploy path already asks ───────────────────────

  `App.vue` calls `enterImmersive` on the deploy path for a handheld, so a battle
  is fullscreen. Everything *before* the battle is not: the sign-in form and the
  garage are browsed with a URL bar at the top and the system gesture area at the
  bottom, which is where the compact garage's own bottom-right controls sit. And
  the bottom edge is not merely occupied — a swipe up from it goes home, so the
  In-battle button shares an edge with "close the game".

  ── Why a button and not a call ─────────────────────────────────────────────

  `requestFullscreen` is only granted from a **user gesture**. Nothing here could
  simply do it on mount, however much better that would read; a prompt whose
  button the player presses *is* the mechanism, not a nag wrapped around one.

  ── When it shows ───────────────────────────────────────────────────────────

  A handheld, in **landscape**, not already fullscreen, and not previously waved
  away. Landscape is the load-bearing condition: `RotatePrompt` owns portrait and
  is an `alertdialog` with no choice in it, so asking here too would stack two
  dialogs and make the first one's instruction unreadable. Rotate first, then
  this — one question at a time.

  ── Two platforms, two offers ────────────────────────────────────────────────

  Where the Fullscreen API exists (`immersiveSupported()`, Android Chrome and
  most others), this asks for it directly — `accept()` below. Where it does not
  — iOS Safari, which implements `requestFullscreen` for a `<video>` and nothing
  else — there is genuinely no button that can invoke real fullscreen, and the
  earlier version of this prompt simply did not offer anything there, which is
  exactly the browser bar the player is asking about. What *does* remove Safari's
  chrome entirely is launching from a Home Screen icon (see
  `apple-mobile-web-app-capable` in `index.html`), so on that platform the same
  dialog explains that instead — Apple exposes no script API for the Share sheet,
  so "tap Share, then Add to Home Screen" is written instruction, not a button.
  `state/immersive.ts#isStandalone` is what stops the game asking again once
  that has actually been done.

  It is deliberately **not** shown in a battle. There is nothing to offer there:
  the deploy path has already asked, every corner of that HUD is spoken for, and
  a panel over a live arena is a panel over the thing it interrupts. The host
  decides, through `v-if`.

  ── Why the refusal is remembered and the acceptance is not ─────────────────

  *No* is a decision about how this player wants to play and must survive a
  reload, or the game asks the same question every visit and becomes something to
  dismiss rather than something to read. *Yes* is not stored at all, because
  fullscreen is left in ways nothing here can see — Escape, the Android back
  gesture, another tab — and `state/immersive.ts` reads the document rather than
  remembering. So the offer simply reappears when the state it is offering is
  genuinely gone, which is the honest behaviour.

  Storage refusing is not an error: `localStorage` throws in some privacy modes,
  and the fallback is a module-scoped flag, so the prompt is dismissible for the
  session even when nothing can be written. It never blocks.
*/
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { wantsTouch } from '@/game/input-source';
import { useDevicePrompt } from '@/state/device-prompts';
import {
  fullscreen,
  isStandalone,
  immersiveSupported,
  toggleImmersive,
} from '@/state/immersive';

const STORAGE_KEY = 'sow.fullscreen-prompt.declined';
const LANDSCAPE_QUERY = '(orientation: landscape)';

/** The session-only fallback for a browser that will not store anything. */
let declinedThisSession = false;

function readDeclined(): boolean {
  if (declinedThisSession) return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

const touch = ref(false);
const landscape = ref(false);
const declined = ref(false);
const supported = immersiveSupported();
/*
 * Read once, like `supported`: the launch surface (a Safari tab versus a
 * Home Screen icon) cannot change without the page reloading into the other
 * one, so there is nothing here for a live listener to catch.
 */
const standalone = isStandalone();

function reread(): void {
  // `wantsTouch()` is a plain function rather than a reactive source and must
  // stay one — see `RotatePrompt.vue`. It is re-read on the same events, plus the
  // harness's own `sow:input-source`, so a forced input source is picked up.
  touch.value = wantsTouch();
  landscape.value =
    typeof window.matchMedia === 'function'
      ? window.matchMedia(LANDSCAPE_QUERY).matches
      : window.innerWidth >= window.innerHeight;
  declined.value = readDeclined();
}

/*
 * `standalone` replaces `supported` in the gate rather than joining it: a
 * Home Screen launch is the state the *other* branch is offering, so once
 * it is true there is nothing left to ask regardless of which branch this
 * platform would have taken.
 */
const showing = computed(
  () => touch.value && landscape.value && !declined.value && !fullscreen.value && !standalone,
);

/*
 * Announced, so the garage's own dialogs wait for this to be answered.
 *
 * The "one question at a time" rule in the header above was enforced only
 * *between* this prompt and the rotate prompt, by the landscape condition. The
 * third dialog — the garage's basic-training offer, which every new phone player
 * meets at the same moment as this one — sat at `z-index: 40` underneath and had
 * its call to action swallowed by this scrim. See `state/device-prompts.ts`.
 */
useDevicePrompt(showing);

let media: MediaQueryList | null = null;

onMounted(() => {
  if (typeof window === 'undefined') return;
  reread();
  if (typeof window.matchMedia === 'function') {
    media = window.matchMedia(LANDSCAPE_QUERY);
    media.addEventListener('change', reread);
  }
  window.addEventListener('resize', reread);
  window.addEventListener('orientationchange', reread);
  window.addEventListener('sow:input-source', reread);
});

onBeforeUnmount(() => {
  media?.removeEventListener('change', reread);
  if (typeof window === 'undefined') return;
  window.removeEventListener('resize', reread);
  window.removeEventListener('orientationchange', reread);
  window.removeEventListener('sow:input-source', reread);
});

async function accept(): Promise<void> {
  // The click *is* the gesture the API requires. Nothing is stored: the state is
  // read from the document, so if it is lost the offer returns by itself.
  await toggleImmersive();
  reread();
}

function decline(): void {
  declinedThisSession = true;
  declined.value = true;
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Session-only, then. Better than refusing to be dismissed.
  }
}
</script>

<template>
  <div
    v-if="showing"
    class="fs-prompt"
    role="dialog"
    aria-modal="true"
    aria-labelledby="fs-prompt-title"
    data-test="fullscreen-prompt"
  >
    <div class="card">
      <template v-if="supported">
        <h2 id="fs-prompt-title">Play fullscreen</h2>
        <p>
          The browser's bars sit exactly where your thumbs go — and a swipe up from
          the bottom edge leaves the game. Fullscreen hides both.
        </p>
        <div class="acts">
          <button
            type="button"
            class="primary"
            data-test="fullscreen-prompt-accept"
            @click="accept"
          >
            Go fullscreen
          </button>
          <button type="button" class="ghost" data-test="fullscreen-prompt-decline" @click="decline">
            Not now
          </button>
        </div>
        <p class="aside">You can change this any time in Settings.</p>
      </template>
      <template v-else>
        <h2 id="fs-prompt-title">Reclaim the screen</h2>
        <p>
          Safari keeps its address bar on screen for a page like this one — there
          is no button that removes it. Adding Steel of War to your Home Screen
          does: it opens as its own full-screen app, with no browser bars at all.
        </p>
        <p class="steps">
          Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
        </p>
        <div class="acts">
          <button
            type="button"
            class="primary"
            data-test="fullscreen-prompt-decline"
            @click="decline"
          >
            Got it
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.fs-prompt {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  padding: 1rem;
  /* Own scrim: this sits over the garage, which is a live WebGL scene, so the
     panel has to be readable over whatever the camera happens to be showing. */
  background: rgba(6, 9, 13, 0.82);
}

.card {
  width: min(30rem, 100%);
  padding: 1.15rem 1.25rem 0.9rem;
  border: 1px solid var(--steel-600, #3a4655);
  border-radius: 0.5rem;
  background: var(--steel-900, #10141a);
  box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.55);
  text-align: center;
}

h2 {
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 400;
}

p {
  margin: 0 0 0.9rem;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--steel-300, #9fb0c0);
}

/* The two named steps, set apart from the explanatory paragraph above them —
   this is the instruction the player actually follows, not more prose. */
.steps {
  margin: 0 0 1rem;
  font-size: 0.9rem;
  color: var(--steel-200, #c7d2dc);
}

.acts {
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* 44px minimum, because this is the first control a phone player ever presses. */
.acts button {
  min-height: 44px;
  min-width: 44px;
  padding: 0 1.1rem;
  border-radius: 0.35rem;
  font: inherit;
  cursor: pointer;
}

.primary {
  border: 1px solid var(--accent, #e8722c);
  background: var(--accent, #e8722c);
  color: #10141a;
}

.ghost {
  border: 1px solid var(--steel-600, #3a4655);
  background: transparent;
  color: var(--steel-200, #c7d2dc);
}

.aside {
  margin: 0.75rem 0 0;
  font-size: 0.72rem;
  color: var(--steel-400, #7d8b99);
}
</style>
